import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from './lib/supabase.js';
import { DataProvider } from './DataContext.jsx';
import App from './app.jsx';
import Login from './Login.jsx';
import { MfaChallenge, MfaEnrollment } from './Mfa.jsx';
import { MFA_ENABLED } from './lib/featureFlags.js';

// Race a promise against a timeout. Mirrors the helper in Mfa.jsx — duplicated
// here so Root doesn't depend on Mfa.jsx for boot-time logic. We've seen
// supabase-js auth calls hang indefinitely after a contended lock, so every
// auth read on the boot path needs a hard ceiling.
async function withTimeout(promise, ms, fallback) {
  let to;
  try {
    return await Promise.race([
      promise,
      new Promise((resolve) => {
        to = setTimeout(() => resolve(fallback), ms);
      }),
    ]);
  } finally {
    clearTimeout(to);
  }
}

// MFA gating — what to show on top of a valid session.
// 'allowed'    — fully authenticated at AAL2 (or no MFA needed)
// 'challenge'  — has factor, needs to step up from AAL1 to AAL2
// 'enroll'     — no verified factor yet, must enroll (mandatory policy)
// 'checking'   — still figuring it out
async function resolveMfaGate() {
  // Master kill-switch: when MFA is disabled we never touch any auth/MFA
  // endpoint — this also sidesteps the client-side hangs those calls caused.
  if (!MFA_ENABLED) return 'allowed';
  try {
    const aalRes = await withTimeout(
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      4000,
      { data: null, error: { message: '__timeout__' } },
    );
    const { data: aalData, error: aalErr } = aalRes;
    console.log('[MFA] AAL resolved:', { aalData, aalErr });
    if (aalErr) {
      console.error('[MFA] AAL check failed/timed out:', aalErr);
      return 'enroll'; // fail-closed: better to nag than to silently bypass
    }
    const { currentLevel, nextLevel } = aalData || {};
    if (currentLevel === 'aal2') return 'allowed';
    if (nextLevel === 'aal2' && currentLevel === 'aal1') return 'challenge';

    // currentLevel === 'aal1' && nextLevel === 'aal1' → no verified factor.
    return 'enroll';
  } catch (err) {
    console.error('[MFA] AAL check threw:', err);
    return 'enroll';
  }
}

function Root() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaGate, setMfaGate] = useState('checking');

  // Enrollment shows the recovery codes AFTER verify() succeeds, but verify()
  // upgrades the session to AAL2 and triggers onAuthStateChange. Without this
  // flag, the auto re-resolve would flip the gate to 'allowed' and unmount
  // MfaEnrollment — losing the recovery-code screen. While locked, the
  // component is in charge of its own lifecycle until it calls onComplete.
  const enrollLockedRef = useRef(false);

  const recheckMfa = useCallback(async () => {
    if (enrollLockedRef.current) return;
    setMfaGate(await resolveMfaGate());
  }, []);

  useEffect(() => {
    // PARANOID ULTIMATE FALLBACK: no matter what happens inside the async
    // boot below — bug in withTimeout, supabase-js explodes, React state
    // updates getting dropped, anything — the "Lade …" screen is GONE
    // after 6 seconds. If we get here with no session, the user lands on
    // Login. If they have a session at AAL1 with a factor, MfaChallenge
    // takes over (which has its own timeouts).
    const hardEscape = setTimeout(() => {
      console.warn('[Root] hard escape from loading state fired (6s)');
      setLoading(false);
    }, 6000);

    (async () => {
      console.log('[Root] boot start');
      try {
        const sessionRes = await withTimeout(
          supabase.auth.getSession(),
          4000,
          { data: { session: null }, error: { message: '__timeout__' } },
        );
        const session = sessionRes?.data?.session ?? null;
        console.log('[Root] session resolved:', { hasSession: !!session, timedOut: sessionRes?.error?.message === '__timeout__' });
        setSession(session);
        if (session) await recheckMfa();
        console.log('[Root] boot done');
      } catch (err) {
        console.error('[Root] initial session/MFA load failed:', err);
      } finally {
        clearTimeout(hardEscape);
        setLoading(false);
      }
    })();
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      setSession(newSession);
      try {
        if (newSession) await recheckMfa();
        else {
          enrollLockedRef.current = false;
          setMfaGate('checking');
        }
      } catch (err) {
        console.error('[Root] auth state change handler failed:', err);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [recheckMfa]);

  async function signOut() {
    enrollLockedRef.current = false;
    await supabase.auth.signOut();
  }

  function lockEnrollment() {
    enrollLockedRef.current = true;
  }

  async function finishEnrollment() {
    enrollLockedRef.current = false;
    setMfaGate(await resolveMfaGate());
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: '#0B0D13', color: 'var(--ink-3)', fontFamily: 'var(--sans)',
      }}>
        <div className="tiny muted">Lade …</div>
      </div>
    );
  }

  if (!session) return <Login />;

  if (mfaGate === 'checking') {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: '#0B0D13', color: 'var(--ink-3)', fontFamily: 'var(--sans)',
      }}>
        <div className="tiny muted">Prüfe Sicherheit …</div>
      </div>
    );
  }

  if (mfaGate === 'challenge') return <MfaChallenge onSignOut={signOut} onComplete={recheckMfa}/>;
  if (mfaGate === 'enroll') {
    return <MfaEnrollment
      onSignOut={signOut}
      onLock={lockEnrollment}
      onComplete={finishEnrollment}
    />;
  }

  return (
    <DataProvider session={session}>
      <App />
    </DataProvider>
  );
}

export default Root;
